"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RanchTypeEnum, User } from "@/api/lctApISchemas";
import { useAppSelector } from "@/hooks/reduxHooks";
import { useToast } from "@/hooks/use-toast";
import { DjRestAuthUserRetrieveError, useDjRestAuthUserPartialUpdate } from "@/api/lctApIComponents";
import { QueryObserverResult, RefetchOptions } from "@tanstack/react-query";

interface IProps {
  defaultValues?: {
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    ranchName: string;
    ranchAddress: string;
    ranchType: "" | RanchTypeEnum | undefined;
  };
  refetch?: (options?: RefetchOptions) => Promise<QueryObserverResult<User, DjRestAuthUserRetrieveError>>;
}

const ranchTypeOptions: { label: string; value: RanchTypeEnum }[] = [
  { label: "Cow-Calf", value: "COW_CALF" },
  { label: "Backgrounder", value: "BACKGROUNDER" },
  { label: "Feedlot", value: "FEEDLOT" },
  { label: "Dairy", value: "DAIRY" },
  { label: "Other", value: "OTHER" },
];

const formSchema = z.object({
  firstName: z.string().min(2, {
    message: "Name must be at least 2 characters.",
  }),
  lastName: z.string().min(2, {
    message: "Name must be at least 2 characters.",
  }),
  email: z.string().email(),
  phoneNumber: z.string(),
  ranchName: z.string(),
  ranchAddress: z.string(),
  ranchType: z.enum(["", "COW_CALF", "BACKGROUNDER", "FEEDLOT", "DAIRY", "OTHER"]),
});

export const AccountForm: React.FC<IProps> = ({ defaultValues, refetch }) => {
  const { toast } = useToast();
  const token = useAppSelector((state) => state.user.authKey);

  const updateUser = useDjRestAuthUserPartialUpdate();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: defaultValues ?? {
      firstName: "",
      lastName: "",
      phoneNumber: "",
      email: "",
      ranchName: "",
      ranchAddress: "",
      ranchType: undefined,
    },
  });

  function onSubmit(values: z.infer<typeof formSchema>) {
    updateUser.mutate(
      {
        headers: { Authorization: `Token ${token}` },
        body: {
          ...(values.firstName && { first_name: values.firstName }),
          ...(values.lastName && { last_name: values.lastName }),
          ...(values.email && { email: values.email }),
          profile: {
            ...(values.phoneNumber && { phone_number: values.phoneNumber }),
            ...(values.ranchName && { ranch_name: values.ranchName }),
            ...(values.ranchAddress && { ranch_address: values.ranchAddress }),
            ...(values.ranchType && { ranch_type: values.ranchType }),
          },
        },
      },
      {
        onSuccess: () => {
          refetch?.();
          toast({
            variant: "default",
            title: "Success",
            description: "Updated your details!",
          });
        },
        onError: (error) => {
          toast({
            variant: "destructive",
            title: "Error",
            description: `${error?.payload}`,
          });
        },
      },
    );
  }

  return (
    <section className="max-w-[560px] rounded-lg border border-slate-300 bg-white p-6">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="h-full space-y-8">
          <h1 className="text-2xl font-bold">Account Details</h1>
          <FormField
            control={form.control}
            name="firstName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>First Name</FormLabel>
                <FormControl>
                  <Input placeholder="John" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="lastName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Last Name</FormLabel>
                <FormControl>
                  <Input placeholder="Smith" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input placeholder="john.smith@gmail.com" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="phoneNumber"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Phone Number</FormLabel>
                <FormControl>
                  <Input placeholder="+1" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="ranchName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Ranch Name</FormLabel>
                <FormControl>
                  <Input placeholder="Smith Ranch" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="ranchAddress"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Ranch Address</FormLabel>
                <FormControl>
                  <Input placeholder="1234 Ranch Rd" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="ranchType"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Ranch Type</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select an option" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {ranchTypeOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button type="submit">Save Changes</Button>
        </form>
      </Form>
    </section>
  );
};
